'use client';

import { RootLoading } from '@workspace/ui';
import { ACCESSIBLE_APPS, isGuestOnlyRoute, isPublicRoute } from '@workspace/ui/lib';
import type { JwtPayload, JwtUser } from '@workspace/ui/types';
import { jwtDecode } from 'jwt-decode';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect } from 'react';
import { clearAuthCookies, logout, refreshToken } from '@/actions/mixed/auth';
import { GUEST_ONLY_ROUTES, PUBLIC_ROUTES } from '@/config/routes';
import { usePermissions } from '@/hooks/use-permission';
import { useAuthStore } from '@/stores/authStore';

const ONBOARDING_CHECK_KEY = 'onboarding_check_completed';

export default function AuthBootstrap({
  initialAccessToken,
  hasRefreshToken,
  children,
}: {
  initialAccessToken?: string;
  hasRefreshToken: boolean;
  children: React.ReactNode;
}) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const setAccessToken = useAuthStore((s) => s.setAccessToken);
  const setIsAuthenticated = useAuthStore((s) => s.setAuthenticated);
  const setIsLoading = useAuthStore((s) => s.setIsLoading);
  const setUser = useAuthStore((s) => s.setUser);
  const user = useAuthStore((s) => s.user);
  const isLoading = useAuthStore((s) => s.isLoading);

  const router = useRouter();
  const pathname = usePathname();

  const { hasPermission } = usePermissions();

  useEffect(() => {
    if (user?.accessible_app !== ACCESSIBLE_APPS.EMPLOYEE) return;

    // Skip if we've already checked for onboarding
    if (localStorage.getItem(ONBOARDING_CHECK_KEY) === 'true') return;

    const canCreateOrganisations = hasPermission('create any organisations');

    if (canCreateOrganisations && user?.organisations.length === 0 && pathname !== '/sign-up') {
      router.push('/sign-up');
    }

    if ((user && !canCreateOrganisations) || (user && user?.organisations.length > 0)) {
      localStorage.setItem(ONBOARDING_CHECK_KEY, 'true');
    }
  }, [user, pathname, router, hasPermission]);

  useEffect(() => {
    // Don't trigger refresh for guest-only routes or when we have an access token
    if (isGuestOnlyRoute(GUEST_ONLY_ROUTES, pathname) || accessToken) {
      setIsLoading(false);
      return;
    }

    if (isPublicRoute(PUBLIC_ROUTES, pathname) && !initialAccessToken && !hasRefreshToken) {
      setIsLoading(false);
      return;
    }

    const abortController = new AbortController();

    const now = Date.now();

    // If we have an initial token, decode it to check expiration
    if (initialAccessToken) {
      try {
        const decodedToken = jwtDecode<JwtPayload>(initialAccessToken);

        const expirationTime = decodedToken.exp * 1000; // Convert to milliseconds

        const user: JwtUser = {
          user: {
            id: Number(decodedToken.sub),
            first_name: decodedToken.user.first_name,
            last_name: decodedToken.user.last_name,
            email: decodedToken.user.email,
          },
          permissions: decodedToken.permissions,
          roles: decodedToken.roles,
          organisations: decodedToken.organisations,
          accessible_app: decodedToken.accessible_app,
          teams: decodedToken.teams,
          has_completed_nps: decodedToken.has_completed_nps,
        };

        if (
          user.accessible_app !== ACCESSIBLE_APPS.EMPLOYER &&
          user.accessible_app !== ACCESSIBLE_APPS.EMPLOYEE &&
          user.accessible_app !== ACCESSIBLE_APPS.CONTRACTOR
        ) {
          // Logout and redirect
          logout().finally(() => {
            localStorage.removeItem('onboarding_check_completed');
            window.location.href = process.env.NEXT_PUBLIC_FE_URL_AUTH!;
          });

          return;
        }

        // Only refresh if we're within 1 minute of expiration
        if (expirationTime - now > 60 * 1000) {
          if (abortController.signal.aborted) return;

          setAccessToken(initialAccessToken);

          setIsAuthenticated(true);

          setUser(user);
          setIsLoading(false);

          return;
        } else {
          refreshToken()
            .then((result) => {
              if (abortController.signal.aborted) return;

              if (!result.success) {
                // Refresh failed, clear auth and redirect
                setAccessToken(null);
                setIsAuthenticated(false);
                setUser(null);
                clearAuthCookies().finally(() => {
                  window.location.href = process.env.NEXT_PUBLIC_FE_URL_AUTH!;
                });
              }
              // On success, refreshToken() already updated the store
            })
            .catch((error) => {
              console.error('Failed to refresh token:', error);

              if (abortController.signal.aborted) return;

              // Handle error same as refresh failure
              setAccessToken(null);
              setIsAuthenticated(false);
              setUser(null);
              clearAuthCookies().finally(() => {
                window.location.href = process.env.NEXT_PUBLIC_FE_URL_AUTH!;
              });
            })
            .finally(() => {
              setIsLoading(false);
            });
        }
      } catch (error) {
        console.error('Failed to decode token:', error);

        if (abortController.signal.aborted) return;

        localStorage.removeItem(ONBOARDING_CHECK_KEY);
        setAccessToken(null);
        setIsAuthenticated(false);
        setUser(null);

        clearAuthCookies().finally(() => {
          window.location.href = process.env.NEXT_PUBLIC_FE_URL_AUTH!;
        });
      }
    } else {
      setIsLoading(false);
    }

    return () => {
      abortController.abort();
    };
  }, [
    pathname,
    accessToken,
    hasRefreshToken,
    setAccessToken,
    setIsAuthenticated,
    setIsLoading,
    setUser,
    initialAccessToken,
  ]);

  if (isLoading) {
    return <RootLoading />;
  }

  return children;
}
